import DataPill from "@components/pills/DataPill";
import { kybStyles } from "../kybStyles";
import { kybService } from "@services/apis/Kyb";
import { KybLinkRecord } from "@services/models/Kyb/GetKybLinks";
import React, { useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import KybInlineEditor from "./KybInlineEditor";

interface UserIdCellRendererProps {
  data: KybLinkRecord;
  onUpdated: (record: KybLinkRecord) => void;
  refreshRecord: (id: KybLinkRecord["id"]) => Promise<void>;
}

const UserIdCellRenderer: React.FC<UserIdCellRendererProps> = ({
  data,
  onUpdated,
  refreshRecord,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const editRef = useRef<HTMLButtonElement>(null);
  const { handleSuccess } = useSnackbarMessage();
  const userId = String(data.user_id ?? "");
  const closeEditor = () => {
    setIsEditing(false);
    requestAnimationFrame(() => editRef.current?.focus());
  };

  if (isEditing) {
    return (
      <KybInlineEditor
        initialValue={userId}
        label="User ID"
        inputMode="numeric"
        onCancel={closeEditor}
        onSave={async (value) => {
          await kybService.mapUserIdToKYB({ userId: value, kybId: data.id });
          onUpdated({
            ...data,
            user_id: value,
            user_email: null,
            user_account_type: null,
          });
          handleSuccess({
            message: userId
              ? "Assigned user updated."
              : "User assigned to KYB.",
          });
          closeEditor();
          void refreshRecord(data.id);
        }}
      />
    );
  }

  return (
    <div className={kybStyles.cell}>
      {userId ? (
        <>
          <span className="flex-1 tabular-nums">
            <DataPill value={userId} tone="blue" />
          </span>
          <button
            ref={editRef}
            type="button"
            className={kybStyles.editIconButton}
            onClick={() => setIsEditing(true)}
            title="Change assigned user"
            aria-label={`Change assigned user ${userId}`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              edit
            </span>
          </button>
        </>
      ) : (
        <button
          ref={editRef}
          type="button"
          className={`${kybStyles.focus} inline-flex items-center gap-1 px-0 py-[5px] border-0 bg-[transparent] text-text-subtle text-[10px] cursor-pointer whitespace-nowrap [font-family:inherit] hover:text-text-navbar-active [&_.material-symbols-outlined]:text-[15px]`}
          onClick={() => setIsEditing(true)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            add
          </span>
          Assign user
        </button>
      )}
    </div>
  );
};

export default UserIdCellRenderer;
